import { BaseService, Service } from "/@/cool";
import type {
	WorkBenchV3PageRequest,
	WorkBenchV3PageResponse,
	WorkBenchV3StudioStatusRequest,
	WorkBenchV3StudioStatusResponse,
	WorkBenchV3CheckRequest,
	WorkBenchV3CheckResponse,
	WorkBenchV3PendingUploadItem,
	WorkBenchV3OssSignRequest,
	WorkBenchV3OssSignResponse,
	WorkBenchV3OssExistsRequest,
	WorkBenchV3OssExistsResponse,
	WorkBenchV3UploadCompleteRequest,
	WorkBenchV3UploadCompleteResponse
} from "../takePhotoWorkBenchV3/types";

@Service("takePhotoWorkBenchV3")
class TakePhotoWorkBenchV3 extends BaseService {
	constructor() {
		super();

		let prefix = "";
		if (this.namespace) {
			prefix = this.namespace.replace(/\//g, ":") + ":";
		}

		this.permission["page"] = prefix + "page";
		this.permission["studioStatus"] = prefix + "studioStatus";
		this.permission["modelingCountToday"] = prefix + "modelingCountToday";
		this.permission["check"] = prefix + "check";
		this.permission["pendingUploads"] = prefix + "pendingUploads";
		this.permission["ossSign"] = prefix + "ossSign";
		this.permission["ossExists"] = prefix + "ossExists";
		this.permission["uploadComplete"] = prefix + "uploadComplete";
		this.permission["ensureCover"] = prefix + "ensureCover";
		/** 离线同步入口权限（与后端菜单权限码约定） */
		this.permission["offlineSync"] = "takephoto:offlineSync";
		this.permission["offlineSyncCreate"] = prefix + "offlineSyncCreate";
	}

	/**
	 * 拍照记录分页列表（customer_p3d_log）
	 * POST /takePhotoWorkBenchV3/page
	 */
	page(data: WorkBenchV3PageRequest): Promise<WorkBenchV3PageResponse> {
		return this.request({
			url: "/page",
			method: "POST",
			data
		});
	}

	/**
	 * 带 AbortSignal 的分页列表，避免快速切换时慢响应覆盖快响应
	 */
	pageWithSignal(
		data: WorkBenchV3PageRequest,
		signal?: AbortSignal
	): Promise<WorkBenchV3PageResponse> {
		return this.request({
			url: "/page",
			method: "POST",
			data,
			signal
		});
	}

	/**
	 * 影棚实时状态 — 已改为前端直连主控 GET /api/getTakePhotoFlow
	 * @deprecated 请使用 service.equipmentMaster.getTakePhotoFlow
	 */
	studioStatus(data: WorkBenchV3StudioStatusRequest): Promise<WorkBenchV3StudioStatusResponse> {
		return this.request({
			url: "/studioStatus",
			method: "POST",
			data
		});
	}

	/**
	 * 今日提交建模笔数（若 /page 已返回 modeling_count_today 可不调用）
	 * POST /takePhotoWorkBenchV3/modelingCountToday
	 */
	modelingCountToday(data: { shop_id: number; eq_id?: number }): Promise<{ count: number }> {
		return this.request({
			url: "/modelingCountToday",
			method: "POST",
			data
		});
	}

	/** 照片审核（仅写 customer_p3d_log.check_status） */
	check(data: WorkBenchV3CheckRequest): Promise<WorkBenchV3CheckResponse> {
		return this.request({
			url: "/check",
			method: "POST",
			data
		});
	}

	/** 待上传订单（check_status=1 且 status=0） */
	pendingUploads(data: {
		shop_id: number;
		eq_id: number;
	}): Promise<WorkBenchV3PendingUploadItem[]> {
		return this.request({
			url: "/pendingUploads",
			method: "POST",
			data
		});
	}

	/** 批量 OSS 直传签名（服务端并发） */
	ossSign(data: WorkBenchV3OssSignRequest): Promise<WorkBenchV3OssSignResponse> {
		return this.request({
			url: "/ossSign",
			method: "POST",
			data,
			timeout: 120000
		});
	}

	/** 检查 OSS 对象是否已存在（断点续传） */
	ossExists(data: WorkBenchV3OssExistsRequest): Promise<WorkBenchV3OssExistsResponse> {
		return this.request({
			url: "/ossExists",
			method: "POST",
			data
		});
	}

	/** 全部上传完成 */
	uploadComplete(
		data: WorkBenchV3UploadCompleteRequest
	): Promise<WorkBenchV3UploadCompleteResponse> {
		return this.request({
			url: "/uploadComplete",
			method: "POST",
			data
		});
	}

	/**
	 * 补生成贴图封面（拍照确认弹窗打开前）
	 * POST /takePhotoWorkBenchV3/ensureCover
	 */
	ensureCover(data: {
		pid: number;
		shop_id: number;
		eq_id: number;
	}): Promise<{ pid: number; guid: string; texture_cover_img: string }> {
		return this.request({
			url: "/ensureCover",
			method: "POST",
			data
		});
	}

	/**
	 * 离线同步：同名同号找/建客户 + 建云端拍照单（queue_id=-1）
	 * POST /takePhotoWorkBenchV3/offlineSyncCreate
	 */
	offlineSyncCreate(data: {
		shop_id: number;
		eq_id: number;
		customer_name: string;
		phone: string;
		address?: string;
		local_pid?: number;
		photo_count?: number;
	}): Promise<{
		customer_id: number;
		cloud_pid: number;
		customer_created: boolean;
	}> {
		return this.request({
			url: "/offlineSyncCreate",
			method: "POST",
			data
		});
	}
}

export default TakePhotoWorkBenchV3;
